// /src/reports/SupplierPaymentsReport.js
import React, { useState, useEffect } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, addDays } from "date-fns";
import { toast } from "react-toastify";

// OneStop week start date
const ONE_STOP_WEEK1_START = new Date("2025-02-23"); // Sunday

function getOneStopWeekInfo(date) {
  const diffDays = Math.floor(
    (date - ONE_STOP_WEEK1_START) / (1000 * 60 * 60 * 24)
  );
  const weekNum = Math.floor(diffDays / 7) + 1;
  const weekStart = addDays(ONE_STOP_WEEK1_START, (weekNum - 1) * 7);
  const weekEnd = addDays(weekStart, 6);
  return { weekNum, weekStart, weekEnd };
}

export default function SupplierPaymentsReport({ fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("daily");

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, viewType]);

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("zreports")
      .select("date, suppliers")
      .gte("date", fromDate)
      .lte("date", toDate);

    if (error) {
      toast.error("Error fetching supplier payments");
      setLoading(false);
      return;
    }

    // Flatten suppliers array into rows
    let processed = [];
    data.forEach((z) => {
      const dateStr = format(new Date(z.date), "yyyy-MM-dd");
      (z.suppliers || []).forEach((s) => {
        processed.push({
          date: dateStr,
          supplier: s.name || "",
          method: s.method || "",
          amount: parseFloat(s.amount) || 0
        });
      });
    });

    // Sort: date ascending, then supplier name
    processed.sort((a, b) => {
      if (a.date === b.date) return a.supplier.localeCompare(b.supplier);
      return new Date(a.date) - new Date(b.date);
    });

    setRows(processed);
    setLoading(false);
  }

  function groupByWeek(data) {
    const grouped = {};
    data.forEach((row) => {
      const { weekNum, weekStart, weekEnd } = getOneStopWeekInfo(
        new Date(row.date)
      );
      const key = `Week ${weekNum} (${format(weekStart, "dd MMM")} - ${format(
        weekEnd,
        "dd MMM"
      )})`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });

    // Sort suppliers alphabetically inside each week
    Object.keys(grouped).forEach((week) => {
      grouped[week].sort((a, b) => a.supplier.localeCompare(b.supplier));
    });

    return grouped;
  }

  function exportCSV() {
    const headers = ["Date/Week", "Supplier", "Method", "Amount (£)"];
    let csvRows = [headers.join(",")];

    if (viewType === "daily") {
      rows.forEach((r) => {
        csvRows.push([
          r.date,
          r.supplier,
          r.method,
          r.amount.toFixed(2)
        ].join(","));
      });
    } else {
      const weeklyGroups = groupByWeek(rows);
      Object.keys(weeklyGroups).forEach((week) => {
        weeklyGroups[week].forEach((r) => {
          csvRows.push([
            week,
            r.supplier,
            r.method,
            r.amount.toFixed(2)
          ].join(","));
        });
      });
    }

    saveAs(
      new Blob([csvRows.join("\n")], { type: "text/csv" }),
      `supplier_payments_${format(new Date(), "yyyyMMdd_HHmm")}.csv`
    );
  }

  if (loading) return <p>Loading Supplier Payments Report...</p>;

  if (viewType === "daily") {
    const totals = rows.reduce(
      (acc, r) => {
        if (r.method === "Cash") acc.cash += r.amount;
        if (r.method === "Bank") acc.bank += r.amount;
        acc.total += r.amount;
        return acc;
      },
      { cash: 0, bank: 0, total: 0 }
    );

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Supplier</th>
              <th>Method</th>
              <th>Amount (£)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{r.supplier}</td>
                <td>{r.method}</td>
                <td>{r.amount.toFixed(2)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
              <td colSpan="2">Total</td>
              <td>Cash: £{totals.cash.toFixed(2)} | Bank: £{totals.bank.toFixed(2)}</td>
              <td>£{totals.total.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    const weeklyGroups = groupByWeek(rows);
    const grandTotals = rows.reduce(
      (acc, r) => {
        if (r.method === "Cash") acc.cash += r.amount;
        if (r.method === "Bank") acc.bank += r.amount;
        acc.total += r.amount;
        return acc;
      },
      { cash: 0, bank: 0, total: 0 }
    );

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        {Object.keys(weeklyGroups).map((week) => {
          const weekRows = weeklyGroups[week];
          const weekTotals = weekRows.reduce(
            (acc, r) => {
              if (r.method === "Cash") acc.cash += r.amount;
              if (r.method === "Bank") acc.bank += r.amount;
              acc.total += r.amount;
              return acc;
            },
            { cash: 0, bank: 0, total: 0 }
          );

          return (
            <div key={week} style={{ marginBottom: "20px" }}>
              <h3>{week}</h3>
              <table>
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Method</th>
                    <th>Amount (£)</th>
                  </tr>
                </thead>
                <tbody>
                  {weekRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.supplier}</td>
                      <td>{r.method}</td>
                      <td>{r.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                    <td colSpan="1">Weekly Total</td>
                    <td>Cash: £{weekTotals.cash.toFixed(2)} | Bank: £{weekTotals.bank.toFixed(2)}</td>
                    <td>£{weekTotals.total.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        <h3 style={{ marginTop: "10px" }}>
          Grand Total — Cash: £{grandTotals.cash.toFixed(2)} | Bank: £{grandTotals.bank.toFixed(2)} | All: £{grandTotals.total.toFixed(2)}
        </h3>
      </div>
    );
  }
}
